/**
 * Frame3_3 - Distribution Plan Overview
 *
 * Purpose: High-level view of how assets are to be distributed
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Users, ArrowRight, ArrowLeft, Gift } from 'lucide-react';
import { Button } from '../ui/Button';

export default function Frame3_3() {
  const navigate = useNavigate();
  const { estateId } = useParams();

  const handleNext = () => {
    navigate(estateId ? `/frame-3-3-1/${estateId}` : '/frame-3-3-1');
  };

  const handleBack = () => {
    navigate(estateId ? `/frame-3-2-1/${estateId}` : '/frame-3-2-1');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Distribution Plan</h1>
          <p className="text-gray-600">
            How the estate assets are to be distributed to beneficiaries
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <Gift className="w-6 h-6 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">Distribution Instructions</h2>
          </div>
          <p className="text-gray-600 mb-4">
            The deceased created a distribution plan specifying how assets should be allocated
            among beneficiaries. This plan serves as your guide during the distribution phase.
          </p>
          <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
            <h3 className="font-semibold text-cyan-900 mb-2">What You'll Find:</h3>
            <ul className="space-y-2 text-sm text-cyan-800">
              <li>• Specific bequests (particular items to particular people)</li>
              <li>• Percentage allocations for residuary estate</li>
              <li>• Primary and contingent beneficiary assignments</li>
              <li>• Special instructions or conditions</li>
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-6 h-6 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">Implementation Notes</h2>
          </div>
          <p className="text-gray-600 mb-4">
            During the distribution phase, you'll use this plan to:
          </p>
          <ul className="space-y-2 text-gray-700">
            <li>• Verify all beneficiaries can be located</li>
            <li>• Calculate exact distribution amounts</li>
            <li>• Track the transfer of each asset</li>
            <li>• Obtain receipts and releases</li>
          </ul>
        </div>

        <div className="flex justify-between items-center">
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button onClick={handleNext}>
            View Distribution Details
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
